import React, { useState, useEffect, useRef } from 'react';
import { GET } from '../services/api';

/**
 * Tenure (chapter term) picker for the report / dashboard filters.
 *
 * Backend contract (see backend/docs/tenure-feature.md section 6):
 *   value ''        -> send no tenureId; the endpoint keeps its normal date filter
 *   value 'current' -> the chapter's active tenure
 *   value '<id>'    -> that specific tenure
 *
 * A tenure belongs to one chapter, so options only load once a single chapter is
 * selected. With no chapter the control stays disabled and explains why, rather than
 * silently offering options that the API would refuse to resolve.
 */
const TenureSelect = ({
  chapterId,
  value,
  onChange,
  label = 'Tenure',
  className = '',
  // Defaults to the report-filter label style; Dashboard passes its own.
  labelClassName = 'block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 ml-1',
  disabled = false,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Consumers often pass an inline arrow; keeping it in a ref stops the effects below
  // from re-running on every parent render.
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  useEffect(() => {
    let cancelled = false;

    if (!chapterId) {
      setOptions([]);
      return undefined;
    }

    (async () => {
      setLoading(true);
      try {
        const res = await GET(`/tenures/options?chapter=${encodeURIComponent(chapterId)}`);
        if (!cancelled) setOptions((res && res.data) || []);
      } catch (err) {
        console.error('Error fetching tenures:', err);
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [chapterId]);

  /**
   * Drop a selection that no longer belongs to the chapter in context. Without this,
   * switching chapter would keep sending the previous chapter's tenureId — which the
   * API resolves happily, producing a window for a term the user is no longer looking
   * at. Covers: chapter cleared, chapter switched, and chapter has no tenures at all.
   */
  useEffect(() => {
    if (!value || value === 'current') {
      if (!chapterId && value) onChangeRef.current('');
      return;
    }
    if (!chapterId) {
      onChangeRef.current('');
      return;
    }
    if (loading) return;
    if (!options.some((t) => t._id === value)) onChangeRef.current('');
  }, [chapterId, value, options, loading]);

  const isDisabled = disabled || !chapterId || loading;

  return (
    <div className={className}>
      <label className={labelClassName}>{label}</label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={isDisabled}
        className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none appearance-none font-medium text-slate-700"
      >
        <option value="">{chapterId ? 'Not by tenure' : 'Select a chapter first'}</option>
        {chapterId && <option value="current">Current tenure</option>}
        {options.map((t) => (
          <option key={t._id} value={t._id}>
            {t.name}{t.status === 'active' ? ' (active)' : t.status === 'upcoming' ? ' (upcoming)' : ''}
          </option>
        ))}
      </select>
      {chapterId && !loading && options.length === 0 && (
        <p className="text-xs text-amber-600 mt-1.5 font-medium">
          No tenure defined for this chapter yet.
        </p>
      )}
    </div>
  );
};

export default TenureSelect;
